<template>
  <div class="breadcrumbs" v-if="breadcrumbs.length > 1 && showBreadcrumbs" :dir="locale === 'ar' ? 'rtl' : 'ltr'">
    <template v-for="(breadcrumb, index) in breadcrumbs" :key="breadcrumb.path">
      <router-link 
        v-if="index < breadcrumbs.length - 1"
        :to="breadcrumb.path"
        class="breadcrumb-link"
      >
        {{ getBreadcrumbTitle(breadcrumb) }}
      </router-link>
      <span v-else class="breadcrumb-current">
        {{ getBreadcrumbTitle(breadcrumb) }}
      </span>
      <span v-if="index < breadcrumbs.length - 1" class="breadcrumb-separator">
        {{ locale === 'ar' ? '<' : '>' }}
      </span>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

const route = useRoute()
const router = useRouter()
const store = useStore()
const { locale, t } = useI18n()

const breadcrumbs = computed(() => {
  const matched = route.matched.filter(record => record.meta?.title || record.meta?.breadcrumb)

  const real = matched.map((record) => {
    let path = record.path
    Object.keys(route.params).forEach(param => {
      path = path.replace(`:${param}`, route.params[param])
    })

    return {
      path: path,
      name: record.name,
      meta: record.meta
    }
  })

  // Some pages are logically nested under a parent listing (e.g. a calculation
  // detail page under the calculations list) but are registered as separate
  // top-level routes rather than actual router children (to avoid disturbing
  // existing route names/links). `meta.breadcrumbParents` lets a route declare
  // synthetic ancestors so the trail still shows without real nesting.
  // breadcrumbParents can be a static array, or a function of the current
  // route (used when the "right" virtual parent depends on a query/param,
  // e.g. the shared salary receipt edit page picking rewards vs deductions).
  const rawParents = route.meta?.breadcrumbParents
  const declaredParents = typeof rawParents === 'function' ? (rawParents(route) || []) : (rawParents || [])
  const virtual = declaredParents.map((parent) => {
    let path = parent.path
    if (!path && parent.name) {
      try {
        path = router.resolve({ name: parent.name }).path
      } catch {
        path = null
      }
    }
    return {
      path: path || '#',
      name: parent.name,
      meta: { title: parent.title }
    }
  })

  return [...virtual, ...real]
})

const showBreadcrumbs = computed(() => {
  const user = store.state.user
  // Hide breadcrumbs if user is guardian and route has hideBreadcrumbForGuardian meta
  if (user?.role?.slug === 'guardian' && route.meta.hideBreadcrumbForGuardian) return false
  // Some sections (e.g. salary management) opt out of the breadcrumb trail entirely
  if (route.meta?.hideBreadcrumb) return false
  return true
})

const getBreadcrumbTitle = (breadcrumb) => {
  // If meta has a translation key, use it
  if (breadcrumb.meta?.breadcrumbKey) {
    return t(breadcrumb.meta.breadcrumbKey)
  }
  
  // If meta has a title, use it (could be a translation key or literal text)
  if (breadcrumb.meta?.title) {
    // Check if the title exists as a translation key
    if (t(breadcrumb.meta.title) !== breadcrumb.meta.title) {
      return t(breadcrumb.meta.title)
    }
    return breadcrumb.meta.title
  }
  
  // Fallback to route name
  return breadcrumb.name
}
</script>

<style scoped>
.breadcrumbs {
  padding: 12px 16px;
  background-color: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

[dir="ltr"] .breadcrumbs {
  justify-content: flex-start;
}

[dir="rtl"] .breadcrumbs {
  justify-content: flex-start;
}

.breadcrumb-link {
  color: #2C91AE;
  text-decoration: none;
  margin: 0 4px;
  font-size: 14px;
}

.breadcrumb-link:hover {
  text-decoration: underline;
}

.breadcrumb-current {
  color: #666;
  margin: 0 4px;
  font-size: 14px;
  font-weight: 500;
}

.breadcrumb-separator {
  color: #666;
  margin: 0 4px;
  font-size: 14px;
}

/* RTL specific separator styling */
[dir="rtl"] .breadcrumb-separator {
  transform: rotate(180deg);
  display: inline-block;
}

/* Ensure proper ordering in RTL */
[dir="rtl"] .breadcrumbs > * {
  display: flex;
  align-items: center;
}
</style>